/**
 * V2.0.29 — Pure-frontend inspector helpers.
 *
 * Live editor stats that don't need a server round-trip:
 *   • Square footage (sum of all room footprints).
 *   • Cost estimate ($/sqft × footprint, style-aware).
 *   • Structural span warnings (rooms wider than typical joist span).
 *
 * Compliance violations require the server (heuristics live in
 * `/app/backend/code_violations.py`) — those are debounced + fetched
 * from `getCodeViolations` separately by the rail component.
 */

// $/sqft heuristic — bands by style, midpoint shown in UI.
//
// V2.0.57 — Recalibrated against 2025 RSMeans + HomeAdvisor + Forbes
// national new-build medians (US, single-family). The prior bands were
// HCOL-coastal-biased ($240-$510), which produced $280-$420 quotes in
// Wilmington NC where the real market is $115-$220. New base bands are
// national-median; a state multiplier (below) lifts or lowers them by
// region. Sources audited Feb 2026 — adjust here if RSMeans publishes
// new indices.
const STYLE_COST_RANGES = {
  modern: [220, 360],
  contemporary: [210, 350],
  traditional: [180, 280],
  craftsman: [190, 290],
  farmhouse: [175, 270],
  colonial: [180, 280],
  cottage: [160, 240],
  ranch: [150, 230],
  midcentury: [200, 310],
  victorian: [230, 360],
  mediterranean: [210, 320],
};
const FALLBACK_COST_RANGE = [180, 280];

// V2.0.57 — Regional cost multipliers, keyed by USPS state code.
// Built from the RSMeans 2025 City Cost Index relative to the national
// 100 baseline. The handful of HCOL outliers (HI, CA, NY, MA) sit at
// 1.30-1.55; low-cost southern + plains states sit at 0.78-0.90.
const STATE_COST_MULTIPLIER = {
  AL: 0.84, AK: 1.28, AZ: 0.97, AR: 0.82, CA: 1.45, CO: 1.08, CT: 1.18,
  DE: 1.02, FL: 0.96, GA: 0.88, HI: 1.55, ID: 0.95, IL: 1.06, IN: 0.90,
  IA: 0.92, KS: 0.86, KY: 0.85, LA: 0.84, ME: 1.02, MD: 1.10, MA: 1.32,
  MI: 0.98, MN: 1.05, MS: 0.80, MO: 0.90, MT: 0.97, NE: 0.90, NV: 1.05,
  NH: 1.08, NJ: 1.22, NM: 0.92, NY: 1.40, NC: 0.84, ND: 0.94, OH: 0.94,
  OK: 0.82, OR: 1.12, PA: 1.04, RI: 1.12, SC: 0.85, SD: 0.92, TN: 0.86,
  TX: 0.92, UT: 1.00, VT: 1.05, VA: 0.96, WA: 1.18, WV: 0.86, WI: 0.98,
  WY: 0.95, DC: 1.32,
};

// V2.0.60 — ZIP-prefix overrides on top of the state multiplier. The
// 3-digit USPS SCF lets us bump expensive metros (NYC, SF Bay, LA,
// Boston, DC, Honolulu) without distorting the broader state average,
// and lift coastal premiums (Wilmington NC's New Hanover County) on top
// of an otherwise-rural state. Values are AGGREGATE multipliers (replace
// the state multiplier entirely, not stacked).
const ZIP_PREFIX_COST_MULTIPLIER = {
  // NYC metro (Manhattan, Brooklyn, Queens, Bronx) — replaces NY 1.40
  "100": 1.70, "101": 1.70, "102": 1.70, "103": 1.45, "104": 1.45,
  "111": 1.50, "112": 1.50, "113": 1.45, "114": 1.45, "116": 1.45,
  // NJ Hudson / Newark — replaces NJ 1.22
  "070": 1.40, "071": 1.40, "072": 1.35, "073": 1.30,
  // Boston metro — replaces MA 1.32
  "021": 1.55, "022": 1.50, "024": 1.42,
  // DC + Bethesda + Arlington — replaces DC 1.32 / MD / VA
  "200": 1.45, "201": 1.40, "202": 1.45, "203": 1.40, "208": 1.40, "209": 1.40,
  // SF Bay Area — replaces CA 1.45
  "940": 1.85, "941": 1.95, "942": 1.85, "943": 1.85, "944": 1.85, "945": 1.85,
  "946": 1.80, "947": 1.80, "948": 1.75, "949": 1.70, "950": 1.70, "951": 1.65,
  // LA + OC — replaces CA 1.45
  "900": 1.60, "901": 1.55, "902": 1.55, "903": 1.50, "904": 1.55, "905": 1.50,
  "906": 1.55, "907": 1.55, "908": 1.50, "909": 1.45, "910": 1.50, "911": 1.45,
  "912": 1.45, "913": 1.45, "917": 1.45, "918": 1.40, "919": 1.40, "920": 1.55,
  "921": 1.55, "922": 1.55, "923": 1.45, "924": 1.45, "925": 1.45, "926": 1.55,
  "927": 1.50, "928": 1.55,
  // Seattle metro — replaces WA 1.18
  "980": 1.45, "981": 1.50, "982": 1.45, "983": 1.40,
  // Honolulu — replaces HI 1.55
  "967": 1.70, "968": 1.70,
  // Aspen / Vail / Boulder — replaces CO 1.08
  "806": 1.35, "816": 1.45,
  // Coastal NC (Wilmington / Outer Banks) — replaces NC 0.84
  "284": 0.95, "279": 0.92, "275": 0.90,
  // Coastal SC (Charleston / Hilton Head) — replaces SC 0.85
  "294": 1.00, "299": 1.05,
  // Coastal FL (Miami, Naples, Palm Beach) — replaces FL 0.96
  "330": 1.20, "331": 1.20, "334": 1.15, "339": 1.18, "341": 1.20,
  // Aspen-adjacent Jackson WY — replaces WY 0.95
  "830": 1.45,
  // Anchorage / rural AK — replaces AK 1.28
  "995": 1.20, "996": 1.20, "997": 1.40, "998": 1.50, "999": 1.65,
};

function _regionMultiplier(blueprint) {
  const meta = blueprint?.build_site_metadata || blueprint?.metadata?.build_site || {};
  const state = String(meta.state || meta.state_code || "").trim().toUpperCase();
  const zip = String(meta.zip_code || meta.zip || "").trim();
  const prefix = zip.slice(0, 3);
  // ZIP-prefix override beats state multiplier.
  if (prefix && ZIP_PREFIX_COST_MULTIPLIER[prefix]) {
    return { mult: ZIP_PREFIX_COST_MULTIPLIER[prefix], state, zipPrefix: prefix };
  }
  if (state && STATE_COST_MULTIPLIER[state]) return { mult: STATE_COST_MULTIPLIER[state], state, zipPrefix: null };
  return { mult: 1.0, state: null, zipPrefix: null };
}

const M2_PER_SQFT = 10.7639;

// Floor-joist max free span by depth (LVL / engineered I-joist averages,
// 30 psf live load, residential). Below this is fine; above triggers a
// "needs a structural beam" warning since the user's draft is asking for
// more clear span than a single joist run can provide.
const MAX_JOIST_FREE_SPAN_M = 4.88; // ~16 ft

export function computeSquareFootage(blueprint) {
  if (!blueprint?.floors) return { totalSqM: 0, totalSqFt: 0, perFloor: [] };
  let totalSqM = 0;
  const perFloor = [];
  for (const f of blueprint.floors) {
    let floorSqM = 0;
    for (const r of f.rooms || []) {
      if (r?.deleted === true) continue;
      const w = Number(r.width) || 0;
      const d = Number(r.depth) || 0;
      if (w > 0 && d > 0) floorSqM += w * d;
    }
    totalSqM += floorSqM;
    perFloor.push({
      floor_index: f.floor_index || perFloor.length + 1,
      sqM: floorSqM,
      sqFt: floorSqM * M2_PER_SQFT,
    });
  }
  return { totalSqM, totalSqFt: totalSqM * M2_PER_SQFT, perFloor };
}

export function estimateCost(blueprint) {
  const { totalSqFt } = computeSquareFootage(blueprint);
  const style = (blueprint?.metadata?.style || "modern").toLowerCase();
  const [baseLo, baseHi] = STYLE_COST_RANGES[style] || FALLBACK_COST_RANGE;
  const { mult, state, zipPrefix } = _regionMultiplier(blueprint);
  const lo = Math.round(baseLo * mult);
  const hi = Math.round(baseHi * mult);
  return {
    totalSqFt,
    perSqFtLow: lo,
    perSqFtHigh: hi,
    totalLow: Math.round(totalSqFt * lo),
    totalHigh: Math.round(totalSqFt * hi),
    style,
    state,
    zipPrefix,
    regionMultiplier: mult,
  };
}

/**
 * Rooms whose narrower dimension exceeds the max free joist span — these
 * need a load-bearing beam or steel girder mid-span. NOT a hard error
 * (the AI may already plan for one), just a flag for the user + their
 * licensed architect.
 */
export function structuralFlags(blueprint) {
  const out = [];
  if (!blueprint?.floors) return out;
  for (const f of blueprint.floors) {
    const fi = f.floor_index || 1;
    for (const r of f.rooms || []) {
      if (r?.deleted === true) continue;
      const w = Number(r.width) || 0;
      const d = Number(r.depth) || 0;
      const minSpan = Math.min(w, d);
      if (minSpan > MAX_JOIST_FREE_SPAN_M) {
        out.push({
          id: r.id,
          name: r.name || `Room ${r.id}`,
          floor_index: fi,
          spanM: minSpan,
          spanFt: minSpan * 3.28084,
          maxFreeSpanM: MAX_JOIST_FREE_SPAN_M,
          maxFreeSpanFt: MAX_JOIST_FREE_SPAN_M * 3.28084,
        });
      }
    }
  }
  return out;
}

export function formatUSD(n) {
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
}
